import type { ParticipantType } from "~/modules/cbm";
import { ErrorCode } from "~/modules/error";

export type EventProperties = Record<string, any>;

export interface AnalyticsInstance {
    track: (eventName: string, additionalValues?: EventProperties) => void;
}

export enum EVENTS {
    AcceptTaskCompleted = "Fsdk AcceptTask",
    RejectTaskCompleted = "Fsdk RejectTask",
    SetAttributesCompleted = "Fsdk SetAttributes",
    SetCurrentActivityCompleted = "Fsdk SetCurrentActivity",
    WrapUpTaskCompleted = "Fsdk WrapUpTask",
    CompleteTaskCompleted = "Fsdk CompleteTask",
    SetTaskAttributesCompleted = "Fsdk SetTaskAttributes",
    StartVoiceTaskTransferCompleted = "Fsdk StartVoiceTaskTransfer",
    CancelVoiceTaskTransfer = "Fsdk CancelVoiceTaskTransfer",
    KickVoiceParticipantCompleted = "Fsdk KickVoiceParticipant",
    EndTaskCompleted = "Fsdk EndTask",
    HoldVoiceParticipantCompleted = "Fsdk HoldVoiceParticipant",
    UnholdVoiceParticipantCompleted = "Fsdk UnholdVoiceParticipant",
    SetWorkerActivityCompleted = "Fsdk SetWorkerActivity",
    SetWorkerAttributesCompleted = "Fsdk SetWorkerAttributes",
    StartOutboundCallCompleted = "Fsdk StartOutboundCall",
    StartOutboundEmailTaskCompleted = "Fsdk StartOutboundEmailTask",
    EndVoiceCallForAllCompleted = "Fsdk EndVoiceCallForAll",
    UpdateTokenCompleted = "Fsdk UpdateToken",
    ClientRolesCompleted = "Fsdk ClientRoles",
    ClientTokenCompleted = "Fsdk ClientToken",
    AddExternalVoiceParticipantCompleted = "Fsdk AddExternalVoiceParticipant",
    MonitorCallCompleted = "Fsdk MonitorCall",
    LeaveConversationCompleted = "Fsdk LeaveConversation",
    PauseConversationCompleted = "Fsdk PauseConversation",
    ResumeConversationCompleted = "Fsdk ResumeConversation",
    GetTaskParticipantsCompleted = "Fsdk GetTaskParticipants",
    StartConversationTransfer = "Fsdk StartConversationTransfer",
    GetConversationByTaskCompleted = "Fsdk GetConversationByTask",
    GetConversationTransfersCompleted = "Fsdk GetConversationTransfers",
    RemoveEmailParticipantCompleted = "Fsdk RemoveEmailParticipant",
    GetConversationsUserCompleted = "Fsdk GetConversationsUser",
    AddEmailParticipantCompleted = "Fsdk AddEmailParticipant",
    GetPausedConversationsCompleted = "Fsdk GetPausedConversations",
    GetCallByTaskCompleted = "Fsdk GetCallByTask",
    AddConversationEventListenerCompleted = "Fsdk AddConversationEventListener",
    AddTaskParticipantListenerCompleted = "Fsdk AddTaskParticipantListener",
    ClientInitialized = "Fsdk ClientInitialized",
    ClientDestroyed = "Fsdk ClientDestroyed",
    ErrorReported = "Fsdk ErrorReported"
}

interface ErrorPayload {
    error?: string;
    warning?: string;
}

interface Payloads {
    [EVENTS.AddEmailParticipantCompleted]: { taskSid?: string; participantLevel?: string; participantSid?: string };
    [EVENTS.GetConversationTransfersCompleted]: { taskSid?: string; withNotes?: boolean };
    [EVENTS.GetConversationByTaskCompleted]: { taskSid?: string; conversationSid?: string };
    [EVENTS.GetConversationsUserCompleted]: {
        userSid?: string;
        identity?: never ;
    };
    [EVENTS.GetPausedConversationsCompleted]: { pageSize?: number; hasPageToken?: boolean };
    [EVENTS.LeaveConversationCompleted]: { taskSid?: string; interactionSid?: string; channelSid?: string };
    [EVENTS.PauseConversationCompleted]: { taskSid?: string; interactionSid?: string; channelSid?: string };
    [EVENTS.RemoveEmailParticipantCompleted]: { taskSid?: string; participantSid?: string };
    [EVENTS.ResumeConversationCompleted]: {
        interactionSid?: string;
        channelSid?: string;
        reservationSid?: string;
        conversationSid?: string;
    };
    [EVENTS.StartConversationTransfer]: {
        taskSid?: string;
        to?: string;
        interactionSid?: string;
        channelSid?: string;
        workerParticipantSid?: string;
        notesSid?: string;
        transferType?: "warm" | "cold";
    };
    [EVENTS.StartOutboundEmailTaskCompleted]: {
        to?: never ;
        taskQueueSid?: string;
        workflowSid?: string;
        taskSid?: string;
        interactionSid?: string;
        reservationSid?: string;
        conversationSid?: string;
    };
    [EVENTS.AcceptTaskCompleted]: { taskSid?: string; reservationSid?: string };
    [EVENTS.RejectTaskCompleted]: { taskSid?: string; reservationSid?: string };
    [EVENTS.EndTaskCompleted]: { taskSid?: string; reservationSid?: string };
    [EVENTS.WrapUpTaskCompleted]: { taskSid?: string; reservationSid?: string };
    [EVENTS.CompleteTaskCompleted]: { taskSid?: string; reservationSids?: string[] };
    [EVENTS.GetTaskParticipantsCompleted]: { taskSid?: string; reservationSid?: string };
    [EVENTS.StartVoiceTaskTransferCompleted]: {
        taskSid?: string;
        reservationSid?: string;
        toSid?: string;
        mode?: "WARM" | "COLD";
    };
    [EVENTS.CancelVoiceTaskTransfer]: { taskSid?: string; reservationSid?: string; outgoingTransferSid?: string };

    [EVENTS.SetWorkerActivityCompleted]: {
        targetWorkerSid?: string;
        activitySid?: string;
    };
    [EVENTS.SetWorkerAttributesCompleted]: {
        targetWorkerSid?: string;
        mergeExisting?: boolean;
    };
    [EVENTS.GetCallByTaskCompleted]: {
        taskSid?: string;
        customVoiceDevice?: boolean;
        callSid?: string;
        isIncomingCall?: boolean;
    };
    [EVENTS.MonitorCallCompleted]: {
        taskSid?: string;
        reservationSid?: string;
        callSid?: string;
    };
    [EVENTS.StartOutboundCallCompleted]: {
        toNumber?: never ;
        fromNumber?: string;
        taskQueueSid?: string;
        workflowSid?: string;
        taskSid?: string;
        customVoiceDevice?: boolean;
        callSid?: string;
        callAcceptedInAnotherDevice?: boolean;
    };

    [EVENTS.AddExternalVoiceParticipantCompleted]: {
        taskSid?: string;
        participantSid?: string;
        participantType?: ParticipantType;
        phoneNumber?: never ;
    };
    [EVENTS.EndVoiceCallForAllCompleted]: {
        taskSid?: string;
        reservationSid?: string;
    };
    [EVENTS.HoldVoiceParticipantCompleted]: {
        taskSid?: string;
        reservationSid?: string;
        targetParticipantSid?: string;
        participantSid?: string;
        participantType?: ParticipantType;
    };
    [EVENTS.KickVoiceParticipantCompleted]: {
        taskSid?: string;
        reservationSid?: string;
        targetParticipantSid?: string;
        participantSid?: string;
        participantType?: ParticipantType;
    };
    [EVENTS.UnholdVoiceParticipantCompleted]: {
        taskSid?: string;
        reservationSid?: string;
        targetParticipantSid?: string;
        participantSid?: string;
        participantType?: ParticipantType;
    };

    [EVENTS.SetAttributesCompleted]: {
        mergeExisting?: boolean;
    };
    [EVENTS.SetCurrentActivityCompleted]: {
        activitySid?: string;
        rejectPendingReservations?: boolean;
    };
    [EVENTS.SetTaskAttributesCompleted]: {
        taskSid?: string;
        mergeExisting?: boolean;
    };
    [EVENTS.AddTaskParticipantListenerCompleted]: {
        eventName?: string;
        taskSid?: string;
        reservationSid?: string;
    };
    [EVENTS.ClientInitialized]: {
        durationMs?: number;
    };
    [EVENTS.ErrorReported]: {
        code?: ErrorCode;
        debugger?: boolean;
        message?: string;
        module?: string;
        reporter?: string;
        resourceSid?: string;
        severity?: string;
        source?: string;
        stackTrace?: string;
        unhandled?: boolean;
        cause?: string;
    };
}

export type EventPayloads = {
    
    [K in EVENTS]: K extends keyof Payloads ? Payloads[K] & ErrorPayload : ErrorPayload;
};

export interface TelemetryKeys {
    analyticsDev: string;
    analyticsStage: string;
    analyticsProd: string;
    telemetryApplicationId: string;
    telemetryClientToken: string;
}
